import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, describeAxiosError, setViewProgram as setViewProgramStorage, getViewProgram } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = unauth
  const [error, setError] = useState("");
  const [viewProgram, setViewProgramState] = useState(() => getViewProgram());

  // The effective program controlling theme + API filtering.
  // Participants are locked to their own program; facilitators/admins can override.
  const canOverride = user && (user.role === "facilitator" || user.role === "admin");
  const effectiveProgram = canOverride && viewProgram ? viewProgram : (user && user.program) || null;
  const isViewingOther = !!(canOverride && viewProgram && viewProgram !== user.program);

  // Apply program theme on body using EFFECTIVE program (override if set)
  useEffect(() => {
    if (effectiveProgram === "women") {
      document.body.dataset.program = "women";
    } else {
      document.body.removeAttribute("data-program");
    }
  }, [effectiveProgram]);

  // Apply visual theme (light/dark)
  useEffect(() => {
    const stored = (typeof window !== "undefined" && window.localStorage.getItem("tcpi-theme")) || null;
    const desired = (user && user.theme) || stored || "light";
    document.body.dataset.theme = desired;
    if (typeof window !== "undefined") {
      window.localStorage.setItem("tcpi-theme", desired);
    }
  }, [user]);

  // Clear any stale override when a non-facilitator/admin logs in
  useEffect(() => {
    if (user && !canOverride && viewProgram) {
      setViewProgramStorage(null);
      setViewProgramState(null);
    }
  }, [user, canOverride, viewProgram]);

  const setViewProgram = useCallback((p) => {
    setViewProgramStorage(p);
    setViewProgramState(p);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      // 401 is expected when unauthenticated; other errors fall through to
      // the same unauthenticated state.
      setUser(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password, pin) => {
    setError("");
    try {
      const body = { email, password };
      if (pin) body.pin = pin;
      const { data } = await api.post("/auth/login", body);
      setUser(data.user);
      return data.user;
    } catch (e) {
      const msg = describeAxiosError(e);
      const err = new Error(msg);
      // Surface structured backend payload so the Login page can detect
      // PIN-required / setup-PIN states without re-parsing the message.
      err.detail = e?.response?.data?.detail;
      err.status = e?.response?.status;
      setError(msg);
      throw err;
    }
  }, []);

  const register = useCallback(async (payload) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", payload);
      setUser(data.user);
      return data.user;
    } catch (e) {
      const msg = describeAxiosError(e);
      const err = new Error(msg);
      err.detail = e?.response?.data?.detail;
      err.status = e?.response?.status;
      setError(msg);
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Network failure on logout is harmless — we still clear local session.
      console.warn("Logout request failed (clearing local session anyway):", err?.message);
    }
    setViewProgramStorage(null);
    setViewProgramState(null);
    setUser(false);
  }, []);

  // Stable action handlers — their identity never changes after first render.
  const actions = useMemo(
    () => ({ login, register, logout, refresh, setViewProgram }),
    [login, register, logout, refresh, setViewProgram],
  );

  const value = useMemo(
    () => ({
      user,
      error,
      ...actions,
      // View-as override (facilitator/admin only)
      viewProgram,
      canOverride,
      effectiveProgram,
      isViewingOther,
    }),
    [user, error, actions, viewProgram, canOverride, effectiveProgram, isViewingOther],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
